import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_STORAGE_KEY = 'prologue.theme';
export const CUSTOM_PALETTE_STORAGE_KEY = 'prologue.theme.custom-palette';
const THEME_BACKGROUND_DB = 'prologue-theme-assets';
const THEME_BACKGROUND_STORE = 'backgrounds';
const MAX_BACKGROUND_BYTES = 5 * 1024 * 1024;
export type ThemeId = 'hand-drawn' | 'dream' | 'starlight' | 'custom';

export interface ThemePreset {
  id: ThemeId;
  name: string;
  description: string;
  swatches: [string, string, string];
  palette: {
    page: string;
    chrome: string;
    nav: string;
    surface: string;
    primary: string;
  };
}

export type ThemePalette = [string, string, string];

export const THEME_PRESETS: ThemePreset[] = [
  { id: 'hand-drawn', name: '手绘', description: '柔和米色与纸张质感的手绘风格', swatches: ['#f6efe1', '#efe3d0', '#7c5a47'], palette: { page: '#f6efe1', chrome: '#efe3d0', nav: '#eadbc5', surface: '#fffaf0', primary: '#7c5a47' } },
  { id: 'dream', name: '梦幻', description: '淡紫、奶油白与柔和粉色', swatches: ['#f4edf8', '#eee2f4', '#8f5f99'], palette: { page: '#f4edf8', chrome: '#eee2f4', nav: '#e8d7ef', surface: '#fffaff', primary: '#8f5f99' } },
  { id: 'starlight', name: '星空', description: '深蓝夜色与清亮星光', swatches: ['#123400', '#034C8C', '#658DA6'], palette: { page: '#123400', chrome: '#034C8C', nav: '#03588C', surface: '#658DA6', primary: '#ffffff' } },
  { id: 'custom', name: '自定义', description: '上传一张图片作为全局背景（不超过 5MB）', swatches: ['#f6efe1', '#fffaf0', '#b8896a'], palette: { page: '#f6efe1', chrome: '#efe3d0', nav: '#eadbc5', surface: '#fffaf0', primary: '#7c5a47' } },
];

function isThemeId(value: string | null): value is ThemeId {
  return THEME_PRESETS.some((theme) => theme.id === value);
}

export function readStoredTheme(): ThemeId {
  if (typeof window === 'undefined') return 'hand-drawn';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isThemeId(stored) ? stored : 'hand-drawn';
}

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
  customBackground: string | null;
  customPalette: ThemePalette | null;
  setCustomBackground: (file: File) => Promise<boolean>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function openBackgroundDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(THEME_BACKGROUND_DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(THEME_BACKGROUND_STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('主题背景存储不可用'));
  });
}

async function loadBackground(): Promise<Blob | undefined> {
  const db = await openBackgroundDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(THEME_BACKGROUND_STORE, 'readonly').objectStore(THEME_BACKGROUND_STORE).get('current');
    request.onsuccess = () => resolve(request.result as Blob | undefined);
    request.onerror = () => reject(request.error ?? new Error('读取主题背景失败'));
  });
}

async function saveBackground(file: File): Promise<void> {
  const db = await openBackgroundDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction(THEME_BACKGROUND_STORE, 'readwrite').objectStore(THEME_BACKGROUND_STORE).put(file, 'current');
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('保存主题背景失败'));
  });
}

function toHex(value: number): string {
  return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
}

function colorVibrancy(rgb: [number, number, number]): number {
  const values = rgb.map((channel) => channel / 255);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const saturation = max === 0 ? 0 : (max - min) / max;
  // 明亮度和饱和度优先，少量保留出现频次，避免大面积深色压过小面积亮色。
  return saturation * (0.4 + max * 0.6);
}

function colorDistance(a: [number, number, number], b: [number, number, number]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/** 从图片采样主色，并生成浅色/主色/深色三枚主题色。 */
export function extractImagePalette(blob: Blob): Promise<ThemePalette> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('图片采样不可用');
        context.drawImage(image, 0, 0, size, size);
        const pixels = context.getImageData(0, 0, size, size).data;
        const buckets = new Map<string, { rgb: [number, number, number]; count: number }>();
        for (let index = 0; index < pixels.length; index += 4) {
          if (pixels[index + 3] < 150) continue;
          const rgb: [number, number, number] = [pixels[index], pixels[index + 1], pixels[index + 2]];
          const key = rgb.map((channel) => Math.round(channel / 24)).join(',');
          const bucket = buckets.get(key);
          if (bucket) bucket.count += 1;
          else buckets.set(key, { rgb, count: 1 });
        }
        const candidates = [...buckets.values()]
          .filter(({ rgb, count }) => count >= 2 && colorVibrancy(rgb) > 0.12)
          .sort((a, b) => {
            const scoreA = colorVibrancy(a.rgb) * (0.82 + Math.min(a.count, 12) / 60);
            const scoreB = colorVibrancy(b.rgb) * (0.82 + Math.min(b.count, 12) / 60);
            return scoreB - scoreA;
          });
        const selected: [number, number, number][] = [];
        for (const candidate of candidates) {
          if (selected.every((color) => colorDistance(color, candidate.rgb) > 42)) {
            selected.push(candidate.rgb);
          }
          if (selected.length === 3) break;
        }
        const fallback = candidates[0]?.rgb ?? [184, 137, 106];
        while (selected.length < 3) selected.push(fallback);
        resolve(selected.map(([red, green, blue]) => `#${toHex(red)}${toHex(green)}${toHex(blue)}`) as ThemePalette);
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(image.src);
      }
    };
    image.onerror = () => reject(new Error('读取图片颜色失败'));
    image.src = URL.createObjectURL(blob);
  });
}

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeId>(readStoredTheme);
  const [customBackground, setCustomBackgroundUrl] = useState<string | null>(null);
  const [customPalette, setCustomPalette] = useState<ThemePalette | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const value = JSON.parse(window.localStorage.getItem(CUSTOM_PALETTE_STORAGE_KEY) ?? 'null');
      return Array.isArray(value) && value.length === 3 ? value as ThemePalette : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let objectUrl: string | null = null;
    void loadBackground().then((blob) => {
      if (!blob) return;
      objectUrl = URL.createObjectURL(blob);
      setCustomBackgroundUrl(objectUrl);
      void extractImagePalette(blob).then(setCustomPalette).catch(() => undefined);
    }).catch(() => undefined);
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const setCustomBackground = async (file: File): Promise<boolean> => {
    if (!file.type.startsWith('image/')) return false;
    if (file.size > MAX_BACKGROUND_BYTES) return false;
    const palette = await extractImagePalette(file);
    await saveBackground(file);
    window.localStorage.setItem(CUSTOM_PALETTE_STORAGE_KEY, JSON.stringify(palette));
    const nextUrl = URL.createObjectURL(file);
    setCustomBackgroundUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return nextUrl;
    });
    setCustomPalette(palette);
    setTheme('custom');
    return true;
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    const rootStyle = document.documentElement.style;
    const customPaletteValues = theme === 'custom' && customPalette ? customPalette : null;
    if (customPaletteValues) {
      const [backgroundColor, primaryColor, accentColor] = customPaletteValues;
      rootStyle.setProperty('--fp-bg-chrome', `color-mix(in srgb, ${backgroundColor} 18%, #fffaf0)`);
      rootStyle.setProperty('--fp-bg-nav', `color-mix(in srgb, ${backgroundColor} 28%, #fffaf0)`);
      rootStyle.setProperty('--fp-bg-surface', `color-mix(in srgb, #fffaf0 88%, ${backgroundColor} 12%)`);
      rootStyle.setProperty('--fp-primary', accentColor || primaryColor);
      rootStyle.setProperty('--fp-primary-soft', `color-mix(in srgb, ${accentColor || primaryColor} 18%, transparent)`);
    } else {
      ['--fp-bg-chrome', '--fp-bg-nav', '--fp-bg-surface', '--fp-primary', '--fp-primary-soft'].forEach((property) => rootStyle.removeProperty(property));
    }
    if (theme === 'custom' && customBackground) {
      document.documentElement.style.setProperty('--fp-page-background', `linear-gradient(rgba(246, 239, 225, .76), rgba(246, 239, 225, .76)), url("${customBackground}") center / cover fixed`);
    } else {
      document.documentElement.style.removeProperty('--fp-page-background');
    }
  }, [theme, customBackground]);

  const value = useMemo(() => ({ theme, setTheme, customBackground, customPalette, setCustomBackground }), [theme, customBackground, customPalette]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme 必须在 ThemeProvider 内使用');
  return context;
}
